import * as XLSX from 'xlsx';
import { hoy } from './format';

export type XlsxCellValue = string | number | boolean | Date | null | undefined;

export interface XlsxColumn<T> {
  header: string;
  value: (row: T) => XlsxCellValue;
  width?: number;
  /** Formato numérico Excel (ej '#,##0', '"$"#,##0', '0.0%'). */
  numFmt?: string;
}

export interface XlsxSheetSpec<T> {
  name: string;
  columns: XlsxColumn<T>[];
  rows: T[];
}

// Formatos útiles
export const FMT_COP = '"$"#,##0';
export const FMT_NUM = '#,##0.##';
export const FMT_INT = '#,##0';
export const FMT_PCT = '0.0%';
export const FMT_DATE = 'yyyy-mm-dd';

function sanitizeSheetName(name: string): string {
  // Excel: máx 31 chars, sin [ ] : * ? / \
  return name.replace(/[\[\]:*?/\\]/g, '_').slice(0, 31) || 'Hoja';
}

function buildSheet<T>(spec: XlsxSheetSpec<T>): XLSX.WorkSheet {
  const headers = spec.columns.map((c) => c.header);
  const data: XlsxCellValue[][] = [headers];
  for (const row of spec.rows) {
    data.push(spec.columns.map((c) => c.value(row) ?? ''));
  }
  const ws = XLSX.utils.aoa_to_sheet(data, { cellDates: true });

  // Anchos de columna
  ws['!cols'] = spec.columns.map((c) => ({ wch: c.width ?? 16 }));

  // Formato numérico por columna
  spec.columns.forEach((col, idx) => {
    if (!col.numFmt) return;
    for (let r = 1; r <= spec.rows.length; r++) {
      const ref = XLSX.utils.encode_cell({ r, c: idx });
      const cell = ws[ref];
      if (cell && (cell.t === 'n' || cell.t === 'd')) {
        cell.z = col.numFmt;
      }
    }
  });

  return ws;
}

export function downloadXlsx(filename: string, sheets: XlsxSheetSpec<any>[]): void {
  if (sheets.length === 0) return;
  const wb = XLSX.utils.book_new();
  const usados = new Set<string>();
  for (const sheet of sheets) {
    let name = sanitizeSheetName(sheet.name);
    let i = 2;
    while (usados.has(name)) name = sanitizeSheetName(`${sheet.name}_${i++}`);
    usados.add(name);
    XLSX.utils.book_append_sheet(wb, buildSheet(sheet), name);
  }
  const safe = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, safe);
}

/** Convierte string/number a número o null si no parsea. */
export function toNum(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Nombre de archivo con fecha de hoy. */
export function fileNameConFecha(base: string): string {
  return `${base}-${hoy()}.xlsx`;
}
